<template>
<main class="container-right-nav">

  <div class="container-header">
    <Breadcrumb :links="routesBread" />
    <h2>Lista de usuarios</h2>
  </div>

  <div class="container-body">

    <div class="table-toolbar">
      <div class="toolbar-left">
        <label>
          Mostrar registros:
          <select v-model="pageSize" @change="onPageSizeChange">
            <option :value="10">10</option>
            <option :value="15">15</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </label>
        </div>
      <div class="toolbar-center">
        <label> Filtro Grupos:
        <select id="group" v-model="selectedGroup" @change="onChangeGroup(selectedGroup)">
          <option value="todas">Cualquier grupo</option>
          <option v-for="item in groups" :key="item.id" :value="item.id">{{ item.name }}</option>
        </select>
        </label>
      </div>
      <div class="toolbar-right">
        <label>
          Buscar:
          <input
            type="text"
            v-model="searchQuery"
            @keyup="onSearchKeyup"
            placeholder="Buscar usuario..."
          />
        </label>
      </div>
    </div>

    <table class="users-table">
      <thead>
        <tr>
          <th>#</th>
          <th class="sortable" @click="onSort('firstname')">
            Nombre <font-awesome-icon :icon="getSortIcon('firstname')" />
          </th>
          <th class="sortable" @click="onSort('lastname')">
            Apellido <font-awesome-icon :icon="getSortIcon('lastname')" />
          </th>
          <th class="sortable" @click="onSort('username')">
            Username <font-awesome-icon :icon="getSortIcon('username')" />
          </th>
          <th class="sortable" @click="onSort('group_name')">
            Grupo <font-awesome-icon :icon="getSortIcon('group_name')" />
          </th>
          <th class="sortable" @click="onSort('created_at')">
            Fecha creación <font-awesome-icon :icon="getSortIcon('created_at')" />
          </th>
          <th class="sortable" @click="onSort('last_login')">
            Último login <font-awesome-icon :icon="getSortIcon('last_login')" />
          </th>
          <th>Acción</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in users" :key="item.id">
          <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
          <td>{{ item.firstname }}</td>
          <td>{{ item.lastname }}</td>
          <td>{{ item.username }}</td>
          <td>{{ userGroupName(item) }}</td>
          <td>{{ dateFormatter(item.created_at) }}</td>
          <td>{{ dateTimeFormatter(item.last_login) }}</td>
          <td class="action-cell">
            <router-link :to="'/usuarios/' + item.id" class="btn-action btn-ver">
              <font-awesome-icon icon="eye" /> Ver
            </router-link>
            <router-link :to="'/usuarios/' + item.id + '/editar-usuario'" class="btn-action btn-edit">
              Editar
            </router-link>
            <router-link :to="'/usuarios/' + item.id + '/eliminar-usuario'" class="btn-action btn-delete">
              Eliminar
            </router-link>
          </td>
        </tr>
        <tr v-if="users.length === 0">
          <td colspan="8">No hay usuarios</td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-show="totalPages !== 0"
      :totalPages="totalPages"
      :currentPage="currentPage"
      :pageSize="pageSize"
      @pagechanged="pageChangedClick"
    />

</div>

  <SideNav class="container-sidenav" :links="routesNav" />

</main>
</template>

<style lang="scss" scoped>
.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  margin-bottom: 1em;
}

/*
Alinear "Mostrar registros" & Filtro Grupos & "Buscar" segun observacion SENAPRED 06/2026 ("2. Los selectores: "Mostrar" y "Filtro de grupo" no se encuentran alineados y tampoco tienen el mismo tamaño de texto:" y "3. El "Buscador de texto" no se encuentra alineado.")
1. Dedicar un div para cada herramienta
2. Agregar </label> para cada div (eso permite que cada div tenga el mismo "contenido" a nivel de estilo)
*/
.toolbar-left,
.toolbar-center,
.toolbar-right{
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75em;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
}

//Observacion SENAPRED 06/2026 ("4. La tabla tiene los textos muy grandes. Quizás podría ser mas pequeño y así mostrar mas registros.")
.users-table th,
.users-table td {
  padding: 0.6em 0.8em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  text-align: left;
  vertical-align: middle;
  font-size: 0.875em;
}

.users-table thead th {
  background: #004B7A;
  color: #fff;
  white-space: nowrap;
  font-weight: 600;
}

.users-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.users-table th.sortable:hover {
  background: #006FB3;
}

.users-table tbody tr:hover {
  background: rgba(0, 111, 179, 0.06);
}

.users-table td.action-cell {
  white-space: nowrap;
}

.users-table td.action-cell .btn-action + .btn-action {
  margin-left: 0.5em;
}

.users-table td:nth-child(6),
.users-table td:nth-child(7) {
  white-space: nowrap;
}
</style>

<script>
import SideNav from '@/components/SideNav.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import Pagination from '@/components/Pagination.vue'

import { formatter } from '@/components/mixin/formatter.js'
import { pagination } from '@/components/mixin/pagination.js'
import { restUser } from '@/components/mixin/restUser.js'
import { restGroup } from '@/components/mixin/restGroup.js'

export default {
  mixins: [formatter, pagination, restUser, restGroup],
  components: { SideNav, Breadcrumb, Pagination },
  data() {
    return {
      selectedGroup: 'todas',
      searchQuery: '',
      sortKey: '',
      sortOrder: 'asc',
      pageSize: 15,
      debounceTimer: null
    }
  },
  computed: {
    routesNav() {
      return [
        { name: 'Usuarios' },
        { name: 'Crear usuario', path: '/usuarios/nuevo-usuario' },
        { name: 'Ver grupos', path: '/usuarios/grupos-de-usuarios' }
        //Trasladar boton 'Crear' segun observacion SENAPRED 06/2026
        //"1. Botón "Crear" no tiene aspecto de botón y no sigue la línea de los otros botones existentes. Tiene bores rectos, no tiene espacio interior y no reacciona igual que los otros. Se sugiere mover al menú de botones laterales"
      ]
    },
    routesBread() {
      return [{ name: 'Usuarios' }]
    }
  },
  methods: {
    fetchUsers() {
      if (this.selectedGroup === 'todas') {
        this.getUsersPages(this.currentPage, this.searchQuery, this.sortKey, this.sortOrder, this.pageSize)
      } else {
        this.getUsersByGroup(this.currentPage, this.selectedGroup, this.searchQuery, this.sortKey, this.sortOrder, this.pageSize)
      }
    },
    pageChangedClick(page) {
      this.currentPage = page
      this.fetchUsers()
    },
    onChangeGroup(idGroup) {
      this.currentPage = 1
      this.selectedGroup = idGroup
      this.fetchUsers()
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.fetchUsers()
    },
    onSearchKeyup() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchUsers()
      }, 300)
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'asc'
      }
      this.currentPage = 1
      this.fetchUsers()
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return 'sort'
      return this.sortOrder === 'asc' ? 'sort-up' : 'sort-down'
    },
    userGroupName(user) {
      const group = this.groups.find((element) => element.id === user.id_group)
      return group ? group.name : 'Grupo desconocido'
    }
  },
  created() {
    if (!this.$store.getters.getPermissions.users) {
      this.$router.push('/pagina-no-encontrada')
    }
  },
  mounted() {
    this.getGroupsNoPages()
    this.fetchUsers()
  }
}
</script>
